import { Box, Grid, SimpleGrid, Text } from '@chakra-ui/react';
import { RSecondTitle } from '@resistapp/client/components/headers/r-second-title';
import { RThirdTitle } from '@resistapp/client/components/headers/r-third-title';
import { DownloadLink } from '@resistapp/client/components/links/download-link';
import { UrlLink } from '@resistapp/client/components/links/url-link';
import { GridCell } from '@resistapp/client/components/shared/grid';
import { GridView } from '@resistapp/client/components/shared/layout';
import { buildCtsFileContent } from '@resistapp/common/cts-file-utils';
import { useMemo } from 'react';
import { useChipContext } from './use-chip-context';

const QA_RULES_16S_STANDARD_CURVES = [
  'Disqualify technical replicates with melting temp outside the tm_min - tm_max range.',
  'Disqualify technical replicates with Cq >= 28.',
  'Disqualify non-ribosomal rRNA targeting assay technical replicates with efficiency outside 1.7 - 2.3.',
  'If three technical replicates are remaining and standard deviation > 0.5, disqualify the replicate furthest from the other two.',
  'If three non-AY1 technical replicates are remaining, disqualify one replicate with efficiency most outside 1.9 - 2.1.',
  'Accept biological replicates that still have at least two Cq values and a standard deviation <= 1.',
  'Report as traces if QA only passes with a secondary Cq < 31 threshold, or when the relative abundance is < 1e-5.',
] as const;

const QA_RULES_AY_STANDARD_CURVES = [
  'Disqualify technical replicates with melting temp outside the tm_min - tm_max range.',
  'Disqualify technical replicates with Cq >= 28.',
  'Disqualify non-ribosomal rRNA targeting assay technical replicates with efficiency outside 1.7 - 2.3.',
  'If three technical replicates are remaining and standard deviation > 0.5, disqualify the replicate furthest from the other two.',
  'If three non-AY1 technical replicates are remaining, disqualify one replicate with efficiency most outside 1.9 - 2.1.',
  'Quantify biological replicates that still have at least two Cq values, a standard deviation <= 1, and a mean Cq below the assay-specific LOQ.',
  'If the same QA pass succeeds but the mean Cq is between the assay-specific LOQ and 28, report detected below LOQ.',
  'Otherwise, consider gene not detected if at least two of three technical replicates either have Cq >= 28, or have null Cq together with NoAmplification or CtIsLarge.',
  'Otherwise, report unclear amplification.',
] as const;

function QaRulesList({ rules }: { rules: readonly string[] }) {
  return (
    <Box as="ol" start={0} pl={4} mt={2}>
      {rules.map(rule => (
        <Box as="li" fontSize="md" mb={1} key={rule}>
          {rule}
        </Box>
      ))}
    </Box>
  );
}

export function ChipInfoAndLinksSection() {
  const { chipInfo, workbookLink, projects, debugData, rejectionById } = useChipContext();

  const labSheetUrl = chipInfo?.labSheetUrl;
  const mmLot = chipInfo?.mmLot;
  const waterLot = chipInfo?.waterLot;
  const glycerolLot = chipInfo?.glycerolLot;
  const antifoamBLot = chipInfo?.antifoamBLot;
  const runDate = chipInfo?.runDate;
  const dispenser = chipInfo?.dispenser;
  const dispenserPrefix = chipInfo?.dispenserPrefix ?? (dispenser != null ? 'MSND' : undefined);
  const cycler = chipInfo?.cycler;

  const debugFilename = chipInfo ? `${chipInfo.name}.cts-debug.txt` : 'cts-debug.txt';
  const debugFileContent = useMemo(
    () =>
      buildCtsFileContent(
        debugData.map(d => d.cts),
        { qaById: rejectionById },
      ),
    [debugData, rejectionById],
  );

  return !chipInfo ? null : (
    <GridView style={{ paddingTop: '40px', paddingBottom: '40px' }}>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
        <Box>
          <RSecondTitle>Chip info</RSecondTitle>
          <Box mt={2}>
            {mmLot && (
              <Text fontSize="md" mb={1}>
                SmartChip TB Green Gene Expression Master Mix Lot: {mmLot}
              </Text>
            )}
            {waterLot && (
              <Text fontSize="md" mb={1}>
                qPCR Water Lot: {waterLot}
              </Text>
            )}
            {glycerolLot && (
              <Text fontSize="md" mb={1}>
                Glycerol Lot: {glycerolLot}
              </Text>
            )}
            {antifoamBLot && (
              <Text fontSize="md" mb={1}>
                Antifoam B Lot: {antifoamBLot}
              </Text>
            )}
            {runDate && (
              <Text fontSize="md" mb={1}>
                Run date: {new Date(runDate).toLocaleDateString()}
              </Text>
            )}
            {dispenser != null && (
              <Text fontSize="md" mb={1}>
                Dispenser: {dispenserPrefix ?? ''}
                {dispenser}
              </Text>
            )}
            {cycler != null && (
              <Text fontSize="md" mb={1}>
                Cycler: {cycler}
              </Text>
            )}
          </Box>
        </Box>
        <Box>
          <RSecondTitle>Links</RSecondTitle>
          <Grid>
            {projects.length > 0 && (
              <GridCell widthpercentage={100}>
                <RThirdTitle>Projects</RThirdTitle>
                <Box display="flex" flexDirection="column" gap={1}>
                  {projects.map(p => (
                    <UrlLink key={p.id} text={p.name} href={`/research/${p.id}`} />
                  ))}
                </Box>
              </GridCell>
            )}
            {workbookLink && (
              <GridCell widthpercentage={100} style={{ marginTop: '10px' }}>
                <RThirdTitle>Lab workbook</RThirdTitle>
                <UrlLink
                  text={
                    workbookLink.name?.trim() ??
                    workbookLink.sampleSheetName?.trim() ??
                    `Workbook ${workbookLink.id.slice(0, 8).toUpperCase()}`
                  }
                  href={`/lab/${workbookLink.id}`}
                />
              </GridCell>
            )}
            {labSheetUrl && (
              <GridCell widthpercentage={100} style={{ marginTop: '10px' }}>
                <RThirdTitle>Lab sheet</RThirdTitle>
                <UrlLink text="Lab sheet" href={labSheetUrl} />
              </GridCell>
            )}
            {debugData.length > 0 && (
              <GridCell widthpercentage={100} style={{ marginTop: '10px' }}>
                <RThirdTitle>Cq debug data</RThirdTitle>
                <DownloadLink filename={debugFilename} getFileContent={() => debugFileContent} />
              </GridCell>
            )}
          </Grid>
        </Box>
      </SimpleGrid>
      <Box mt={8}>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8}>
          <Box>
            <RSecondTitle>QA Rules (16S standard curve)</RSecondTitle>
            <QaRulesList rules={QA_RULES_16S_STANDARD_CURVES} />
          </Box>
          <Box>
            <RSecondTitle>QA Rules (assay specific standard curves)</RSecondTitle>
            <QaRulesList rules={QA_RULES_AY_STANDARD_CURVES} />
          </Box>
        </SimpleGrid>
      </Box>
    </GridView>
  );
}
